import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { useTheme } from './hooks/useTheme';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import DiscreetLoginLink from './components/DiscreetLoginLink';
import ProtectedRoute from './components/ProtectedRoute';
import RouteTracker from './components/RouteTracker';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Specialties from './pages/Specialties';
import SpecialtyDetail from './pages/SpecialtyDetail';
import Contact from './pages/Contact';
import Book from './pages/Book';
import Collections from './pages/Collections';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import Debug from './pages/Debug';
import ComingSoon from './pages/ComingSoon';
import './App.css';

function AppShell() {
  useTheme();
  const { settings, loading: settingsLoading } = useSiteSettings();
  const { isAdmin, isAuthenticated, loading: authLoading } = useAuth();

  if (settingsLoading || authLoading) return null;

  const inComingSoon = settings?.comingSoonMode && !isAdmin;
  const showDiscreetLogin = !inComingSoon && !isAuthenticated;
  const shopVisible = settings?.shopVisible !== false;

  return (
    <div className="App">
      <RouteTracker />
      {!inComingSoon && <Navigation />}
      <Routes>
        {inComingSoon ? (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<ComingSoon />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/specialties" element={<Specialties />} />
            <Route path="/specialties/:slug" element={<SpecialtyDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/book" element={<Book />} />
            <Route path="/shop" element={shopVisible ? <Collections /> : <Navigate to="/" replace />} />
            <Route path="/shop/products" element={shopVisible ? <Products /> : <Navigate to="/" replace />} />
            <Route path="/shop/products/:slug" element={shopVisible ? <ProductDetail /> : <Navigate to="/" replace />} />
            {/* legacy redirects — only URLs that existed before the pivot */}
            <Route path="/collections" element={<Navigate to={shopVisible ? '/shop' : '/'} replace />} />
            <Route path="/products" element={<Navigate to={shopVisible ? '/shop/products' : '/'} replace />} />
            <Route path="/products/:slug" element={shopVisible ? <NavigateProduct /> : <Navigate to="/" replace />} />

            <Route path="/login" element={<Login />} />
            <Route path="/debug" element={<Debug />} />
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
      {!inComingSoon && <Footer />}
      {showDiscreetLogin && <DiscreetLoginLink />}
    </div>
  );
}

function NavigateProduct() {
  const { slug } = useParams();
  return <Navigate to={`/shop/products/${slug}`} replace />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <SiteSettingsProvider>
          <AppShell />
        </SiteSettingsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
